/**
 * 剪辑池重复素材检测工具
 *
 * 判定依据：同源视频（transcription_id || video_id）+ 时间区间重叠
 * - 完全重复（dup）：start/end 基本一致（容差 300ms），或重叠比例 >= 90%
 * - 部分重叠（overlap）：重叠时长 >= 200ms 且 >= 较短片段时长的 10%
 *
 * 互相重叠的片段按「连通分量」归为一组，同组分配同一颜色，
 * 便于用户在界面上一眼看出哪些片段互为重复。
 */

// 预置高对比色，用于标记重复组（同一组颜色一致）
export const DUP_COLORS = [
  '#f44336', // 红
  '#ff9800', // 橙
  '#9c27b0', // 紫
  '#e91e63', // 粉
  '#00bcd4', // 青
  '#ff5722', // 深橙
  '#8bc34a', // 绿
  '#3f51b5'  // 蓝
]

const SAME_RANGE_TOLERANCE_MS = 300
const MIN_OVERLAP_MS = 200
const MIN_OVERLAP_RATIO = 0.1
const FULL_OVERLAP_RATIO = 0.9

// 片段来源标识（同一段素材必同源）
function getSourceKey(seg) {
  return seg.transcription_id || seg.video_id || seg.id
}

// 获取可比较的时间区间，无有效区间则返回 null
function getRange(seg) {
  const start = Number(seg.start_time)
  const end = Number(seg.end_time)
  if (isNaN(start) || isNaN(end)) return null
  return { start, end }
}

// 判断两个区间重叠类型，不重叠返回 null
function classifyOverlap(a, b) {
  const overlapStart = Math.max(a.start, b.start)
  const overlapEnd = Math.min(a.end, b.end)
  const overlap = overlapEnd - overlapStart
  if (overlap <= 0) return null

  // 区间基本一致 → 完全重复
  if (
    Math.abs(a.start - b.start) <= SAME_RANGE_TOLERANCE_MS &&
    Math.abs(a.end - b.end) <= SAME_RANGE_TOLERANCE_MS
  ) {
    return 'dup'
  }

  const aDur = a.end - a.start
  const bDur = b.end - b.start
  const minDur = Math.min(aDur, bDur)
  if (minDur <= 0) return null
  const ratio = overlap / minDur

  // 几乎完全覆盖（较短片段被完全包含）→ 完全重复
  if (ratio >= FULL_OVERLAP_RATIO) return 'dup'
  // 达到最小阈值 → 部分重叠
  if (overlap >= MIN_OVERLAP_MS && ratio >= MIN_OVERLAP_RATIO) return 'overlap'
  return null
}

/**
 * 分析一组片段中的重复/重叠情况
 * @param {...Array} segmentArrays 可传多个片段数组（如所有组）
 * @returns {{ dupMap: Object, segById: Object }}
 *   dupMap: { [segmentId]: { color, type: 'dup'|'overlap', count, pairIds, memberIds } }
 */
export function analyzeDuplicates(...segmentArrays) {
  const segById = {}
  const ranges = {}
  const bySource = {}

  for (const arr of segmentArrays) {
    if (!Array.isArray(arr)) continue
    for (const seg of arr) {
      if (!seg || !seg.id) continue
      const range = getRange(seg)
      if (!range) continue
      const key = String(getSourceKey(seg) ?? '')
      segById[seg.id] = seg
      ranges[seg.id] = range
      if (!bySource[key]) bySource[key] = []
      bySource[key].push(seg)
    }
  }

  // 建无向图：边表示两片段存在重叠
  const graph = {}
  const edgeType = {}
  const addEdge = (aId, bId, type) => {
    if (!graph[aId]) graph[aId] = new Set()
    if (!graph[bId]) graph[bId] = new Set()
    graph[aId].add(bId)
    graph[bId].add(aId)
    edgeType[`${aId}|${bId}`] = type
    edgeType[`${bId}|${aId}`] = type
  }

  for (const list of Object.values(bySource)) {
    if (list.length < 2) continue
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]
        const b = list[j]
        const type = classifyOverlap(ranges[a.id], ranges[b.id])
        if (type) addEdge(a.id, b.id, type)
      }
    }
  }

  // 连通分量着色（同一重复组颜色一致）
  const dupMap = {}
  const visited = new Set()
  let colorIdx = 0
  for (const id of Object.keys(graph)) {
    if (visited.has(id)) continue
    // BFS 收集连通分量
    const comp = []
    const queue = [id]
    visited.add(id)
    while (queue.length) {
      const cur = queue.shift()
      comp.push(cur)
      for (const nb of graph[cur] || []) {
        if (!visited.has(nb)) {
          visited.add(nb)
          queue.push(nb)
        }
      }
    }
    const color = DUP_COLORS[colorIdx % DUP_COLORS.length]
    const groupId = colorIdx
    colorIdx++
    for (const memberId of comp) {
      // 组内类型取最严重者（dup 优先于 overlap）
      let type = 'overlap'
      for (const nb of graph[memberId] || []) {
        if (edgeType[`${memberId}|${nb}`] === 'dup') {
          type = 'dup'
          break
        }
      }
      dupMap[memberId] = {
        groupId,
        color,
        type,
        count: comp.length,
        pairIds: [...(graph[memberId] || [])],
        memberIds: [...comp]
      }
    }
  }

  return { dupMap, segById }
}
